// PDF della proforma, condiviso tra TabContabilita (mandati) e TabContabilitaServizio
// (servizi fiduciari). Stesso layout del PDF dei preventivi, con in più il blocco
// pagamenti ricevuti / saldo tra i totali e il bonifico.
// Restituisce { blob, filename }.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ProformaRow {
    id: string;
    descrizione: string;
    quantita: string;
    tariffa: string;
    importo: number;
    inTotal: boolean;          // false = riga decorativa, non contribuisce al totale
    isSection?: boolean;       // riga intestazione di sezione (es. 'PRESTAZIONI')
    isExtra?: boolean;         // riga aggiunta manualmente dall'utente in anteprima
    noIva?: boolean;           // nel totale ma fuori dalla base IVA (rimborsi spese documentate)
}

export interface TotaliProforma {
    totaleBase: number;   // somma righe inTotal (imponibile complessivo)
    esclusi: number;      // quota delle righe inTotal && noIva (esclusa da IVA)
    soggetto: number;     // totaleBase - esclusi (imponibile soggetto a IVA)
    iva: number;          // IVA proforma (0 se nascondiIva)
    totale: number;       // totaleBase + iva
}

const r2 = (x: number) => Math.round(x * 100) / 100;

/** Calcolo puro del box totali della proforma (testabile senza jsPDF). */
export const calcolaTotaliProforma = (
    rows: ProformaRow[], aliquotaIva: number, nascondiIva = false, ivaExtra = 0,
): TotaliProforma => {
    const totaleBase = rows.reduce((s, r) => s + (r.inTotal ? Number(r.importo) || 0 : 0), 0);
    const esclusi = rows.reduce((s, r) => s + (r.inTotal && r.noIva ? Number(r.importo) || 0 : 0), 0);
    const soggetto = totaleBase - esclusi;
    const iva = nascondiIva ? 0 : r2(soggetto * (aliquotaIva / 100) + ivaExtra);
    return { totaleBase, esclusi, soggetto, iva, totale: r2(totaleBase + iva) };
};

export interface PagamentoRow {
    data: string;              // 'YYYY-MM-DD' o ISO
    descrizione: string;
    importo: number;
}

export interface GeneraProformaInput {
    rows: ProformaRow[];
    mode: 'completo' | 'saldo';
    settings: any;
    cliente: any;
    documentTitle?: string;
    docNumber: string;
    oggetto: string[];
    safeName: string;
    aliquotaIva: number;
    totaleIncassi: number;
    pagamenti: PagamentoRow[];
    nascondiIva?: boolean;   // proforma senza IVA: box totali = solo TOTALE (imponibile)
    mostraPagamenti?: boolean; // false nasconde pagamenti ricevuti e saldo (default true)
    // IVA extra non proporzionale all'imponibile (rettifiche "Solo IVA"), così il
    // totale coincide con calcolaCanali. Default 0.
    ivaExtra?: number;
}

// Riquadro dell'intestazione, uguale a quello della relazione: 18,5 × 3,3 cm, centrato.
const HEADER_IMG_TOP = 5;
const HEADER_IMG_W = 185;
const HEADER_IMG_H = 33;

// Disegna l'immagine dentro il riquadro senza deformarla: un'immagine nelle proporzioni del
// riquadro (es. 1850×330 px) lo riempie, le altre restano centrate con bordo bianco.
const drawHeaderImage = (doc: jsPDF, b64: string, pageW: number) => {
    let w = HEADER_IMG_W;
    let h = HEADER_IMG_H;
    try {
        const p = doc.getImageProperties(b64);
        if (p.width > 0 && p.height > 0) {
            const ratio = p.width / p.height;
            if (ratio > HEADER_IMG_W / HEADER_IMG_H) h = HEADER_IMG_W / ratio;
            else w = HEADER_IMG_H * ratio;
        }
    } catch { /* proporzioni ignote: riquadro pieno */ }
    const x = (pageW - w) / 2;
    const y = HEADER_IMG_TOP + (HEADER_IMG_H - h) / 2;
    doc.addImage(b64, 'JPEG', x, y, w, h);
};

// Converte un URL immagine in dataURL base64 per jsPDF.
const imgUrlToBase64 = (url: string): Promise<string | null> => {
    return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                canvas.getContext('2d')!.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/jpeg', 0.92));
            } catch { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });
};

const formatEur = (v: number) => `€ ${v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const generaProformaPdf = async (
    input: GeneraProformaInput,
): Promise<{ blob: Blob; filename: string } | null> => {
    const {
        rows, mode, settings, cliente,
        documentTitle = 'FATTURA PROFORMA',
        docNumber, oggetto, safeName,
        aliquotaIva, totaleIncassi, pagamenti,
        nascondiIva = false, mostraPagamenti = true,
        ivaExtra = 0,
    } = input;

    const totaleBase = rows.reduce((s, r) => s + (r.inTotal ? Number(r.importo) || 0 : 0), 0);
    if (totaleBase <= 0 && rows.length === 0) return null;

    try {
        // La proforma non ha footer: si precarica solo l'header.
        const headerUrl = settings?.mandatoHeaderUrl || (settings as any)?.mandatoHeaderImageUrl || '';
        const [headerB64] = await Promise.all([
            headerUrl ? imgUrlToBase64(headerUrl) : Promise.resolve(null),
        ]);

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const PAGE_W = 210, PAGE_H = 297, MARGIN = 14;
        const CONTENT_W = PAGE_W - MARGIN * 2;

        // Stessa palette del preventivo.
        const COLOR_PRIMARY: [number, number, number] = [31, 41, 55];
        const COLOR_ACCENT:  [number, number, number] = [156, 163, 175];
        const COLOR_DARK:    [number, number, number] = [17, 24, 39];
        const COLOR_MID:     [number, number, number] = [107, 114, 128];
        const COLOR_LIGHT:   [number, number, number] = [249, 250, 251];
        const COLOR_WHITE:   [number, number, number] = [255, 255, 255];

        // Dati agenzia dai settings
        const agencyName    = settings?.agencyName    || 'Agenzia Investigativa';
        const agencyAddress = settings?.address       || '';
        const agencyCity    = settings?.city          || '';
        const agencyPhone   = settings?.phone         || '';
        const agencyEmail   = settings?.email         || '';
        const agencyVat     = settings?.vatNumber     || '';
        const agencyWeb     = settings?.website       || '';

        // Dati cliente
        const nomeCliente = cliente?.tipo === 'Azienda' && cliente?.ragioneSociale
            ? cliente.ragioneSociale
            : `${cliente?.nome || ''} ${cliente?.surname || ''}`.trim() || cliente?.displayName || '';
        const emailCliente    = cliente?.email    || '';
        const telefonoCliente = cliente?.telefono || '';
        const indirizzoClientePdf = [cliente?.indirizzo, cliente?.cap, cliente?.citta, cliente?.provincia]
            .filter(Boolean).join(', ');
        const cfPivaClientePdf = cliente?.partitaIva || cliente?.codiceFiscale || '';

        const oggi = new Date();
        const dataStr = oggi.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

        // Intestazione nello stesso riquadro della relazione (18,5 × 3,3 cm, centrato), così la
        // stessa immagine si vede uguale su relazione, preventivo e proforma. Proporzioni mantenute.
        const HEADER_H = headerB64 ? HEADER_IMG_TOP + HEADER_IMG_H : 30;
        // Nessun footer.
        const FOOTER_H = 0;

        // Il nome è rimasto drawHeaderFooter per non toccare i call site di didDrawPage.
        const drawHeaderFooter = (_pageNum: number, _totalPages: number) => {
            if (headerB64) {
                drawHeaderImage(doc, headerB64, PAGE_W);
            } else {
                doc.setFillColor(...COLOR_PRIMARY); doc.rect(0, 0, PAGE_W, 28, 'F');
                doc.setFillColor(...COLOR_ACCENT);  doc.rect(0, 28, PAGE_W, 2, 'F');
                doc.setTextColor(...COLOR_WHITE);
                doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
                doc.text(agencyName.toUpperCase(), MARGIN, 11);
                doc.setFontSize(8); doc.setFont('helvetica', 'normal');
                const tagRight = `${agencyAddress ? agencyAddress + '  |  ' : ''}${agencyCity}`;
                doc.text(tagRight, PAGE_W - MARGIN, 11, { align: 'right' });
                const contacts = [agencyPhone, agencyEmail, agencyWeb].filter(Boolean).join('   |   ');
                doc.setFontSize(7);
                doc.text(contacts, MARGIN, 20);
                if (agencyVat) doc.text(`P.IVA: ${agencyVat}`, PAGE_W - MARGIN, 20, { align: 'right' });
            }
            doc.setTextColor(...COLOR_DARK);
        };

        // Corpo pagina
        // Spaziature compatte: senza footer la pagina si usa fino a 8mm dal bordo,
        // così la proforma sta di norma in una pagina.
        let cursorY = HEADER_H + 8;
        const CONTENT_BOTTOM_LIMIT = PAGE_H - FOOTER_H - 8;
        const ensureSpace = (needed: number) => {
            if (cursorY + needed > CONTENT_BOTTOM_LIMIT) {
                doc.addPage();
                cursorY = HEADER_H + 8;
            }
        };

        // Titolo documento
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.setTextColor(...COLOR_PRIMARY);
        doc.text(documentTitle, MARGIN, cursorY);

        // Numero e data, allineati a destra
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...COLOR_MID);
        doc.text(`N° ${docNumber}`, PAGE_W - MARGIN, cursorY - 4, { align: 'right' });
        doc.text(`Data emissione: ${dataStr}`, PAGE_W - MARGIN, cursorY + 1, { align: 'right' });

        cursorY += 5;

        // Linea separatrice
        doc.setDrawColor(...COLOR_ACCENT);
        doc.setLineWidth(0.4);
        doc.line(MARGIN, cursorY, PAGE_W - MARGIN, cursorY);
        cursorY += 5;

        // Box cliente
        const BOX_H = 31;
        doc.setFillColor(...COLOR_LIGHT);
        doc.roundedRect(MARGIN, cursorY, CONTENT_W, BOX_H, 2, 2, 'F');
        doc.setDrawColor(209, 213, 219);
        doc.setLineWidth(0.3);
        doc.roundedRect(MARGIN, cursorY, CONTENT_W, BOX_H, 2, 2, 'S');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...COLOR_MID);
        doc.text('INTESTATARIO', MARGIN + 4, cursorY + 5);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...COLOR_DARK);
        doc.text(nomeCliente || '—', MARGIN + 4, cursorY + 11);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...COLOR_MID);
        if (indirizzoClientePdf) doc.text(indirizzoClientePdf, MARGIN + 4, cursorY + 16.5);
        const clientContacts = [emailCliente, telefonoCliente].filter(Boolean).join('   ·   ');
        if (clientContacts) doc.text(clientContacts, MARGIN + 4, cursorY + 21.5);
        if (cfPivaClientePdf) {
            doc.setFontSize(7.5);
            doc.text(`C.F./P.IVA: ${cfPivaClientePdf}`, MARGIN + 4, cursorY + 26.5);
        }

        // Niente "valido per 30 giorni": su una proforma sembrerebbe un termine di pagamento.

        cursorY += BOX_H + 6;

        // Oggetto: righe libere passate dal chiamante (es. "Pratica: XYZ").
        if (oggetto && oggetto.length > 0) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(...COLOR_MID);
            doc.text('OGGETTO', MARGIN, cursorY);
            cursorY += 4;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_DARK);
            oggetto.forEach(line => {
                doc.text(line, MARGIN, cursorY);
                cursorY += 4.2;
            });
            cursorY += 2;
        }

        // Tabella prestazioni a 6 colonne; "Unità" e "Sconto" qui non hanno dati e vanno a "—".
        const tableBody: any[] = rows.map(r => {
            if (r.isSection) {
                return [{
                    content: r.descrizione, colSpan: 6,
                    styles: { fontStyle: 'bold' as any, fillColor: [243, 244, 246] as any, textColor: COLOR_MID as any, fontSize: 8, halign: 'left' as any },
                }];
            }
            const importoNum = Number(r.importo) || 0;
            // L'Helvetica di jsPDF (WinAnsi) non ha il segno meno Unicode U+2212:
            // si usa il "-" ASCII.
            const importoStr = importoNum < 0
                ? `- ${formatEur(Math.abs(importoNum))}`
                : formatEur(importoNum);
            return [
                r.descrizione,
                r.quantita || '—',
                '—',
                r.tariffa || '—',
                '—',
                importoStr,
            ];
        });

        autoTable(doc, {
            startY: cursorY,
            // Margine inferiore come CONTENT_BOTTOM_LIMIT.
            margin: { left: MARGIN, right: MARGIN, top: HEADER_H + 8, bottom: FOOTER_H + 8 },
            head: [['Descrizione', 'Q.tà', 'Unità', 'P. Unit.', 'Sconto', 'Totale']],
            body: tableBody,
            styles: {
                fontSize: 8.5,
                cellPadding: { top: 1.4, bottom: 1.4, left: 4, right: 4 },
                textColor: [17, 24, 39],
                lineColor: [229, 231, 235],
                lineWidth: 0.2,
            },
            headStyles: {
                fillColor: COLOR_PRIMARY,
                textColor: COLOR_WHITE,
                fontStyle: 'bold',
                fontSize: 8,
                cellPadding: { top: 2, bottom: 2, left: 4, right: 4 },
            },
            alternateRowStyles: { fillColor: [249, 250, 251] },
            columnStyles: {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 18, halign: 'center' },
                2: { cellWidth: 18, halign: 'center' },
                3: { cellWidth: 26, halign: 'right' },
                4: { cellWidth: 18, halign: 'center', textColor: [220, 38, 38] },
                5: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
            },
            didParseCell: (data) => {
                if (data.column.index === 5 && data.section === 'body') {
                    data.cell.styles.textColor = COLOR_PRIMARY;
                }
            },
            didDrawPage: () => drawHeaderFooter((doc as any).internal.getCurrentPageInfo().pageNumber, doc.getNumberOfPages()),
        });
        cursorY = (doc as any).lastAutoTable.finalY + 6;

        // Box totali a destra. Con "nascondi IVA" l'IVA è zero e il totale coincide con l'imponibile.
        // Le righe noIva (rimborsi spese) entrano nel totale ma non nella base IVA.
        const totali = calcolaTotaliProforma(rows, aliquotaIva, nascondiIva, ivaExtra);
        const ivaProforma = totali.iva;
        const totaleIvatoProforma = totali.totale;
        const hasEsclusi = totali.esclusi > 0.005;
        const saldoDaIncassareLocal = totaleIvatoProforma - totaleIncassi;
        const isSaldatoLocal = saldoDaIncassareLocal <= 0 && totaleIvatoProforma > 0;
        const totaleProforma = mode === 'saldo' ? Math.max(0, saldoDaIncassareLocal) : totaleIvatoProforma;
        const labelTotale = mode === 'saldo' ? 'SALDO DA VERSARE' : 'TOTALE';

        ensureSpace(nascondiIva ? 19 : (hasEsclusi ? 49 : 41));
        const TOTALS_W = 82;
        const TOTALS_X = PAGE_W - MARGIN - TOTALS_W;
        // I pagamenti ricevuti vanno a sinistra del box totali, alla stessa altezza.
        const totalsTop = cursorY;
        const totalsPage = (doc as any).internal.getCurrentPageInfo().pageNumber;

        if (nascondiIva) {
            // Solo la barra TOTALE, senza righe Imponibile/IVA.
            doc.setFillColor(...COLOR_PRIMARY);
            doc.roundedRect(TOTALS_X, cursorY, TOTALS_W, 15, 2, 2, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.setTextColor(...COLOR_WHITE);
            doc.text(labelTotale, TOTALS_X + 4, cursorY + 10);
            doc.setFontSize(13);
            doc.text(formatEur(totaleProforma), TOTALS_X + TOTALS_W - 4, cursorY + 10, { align: 'right' });
            cursorY += 19;
        } else if (hasEsclusi) {
        // Variante a 3 righe (box 44mm): imponibile soggetto a IVA, spese escluse da IVA, IVA.
        doc.setFillColor(...COLOR_LIGHT);
        doc.roundedRect(TOTALS_X, cursorY, TOTALS_W, 44, 2, 2, 'F');
        doc.setDrawColor(209, 213, 219);
        doc.setLineWidth(0.3);
        doc.roundedRect(TOTALS_X, cursorY, TOTALS_W, 44, 2, 2, 'S');

        const rigaTot = (label: string, val: number, y: number) => {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_MID);
            doc.text(label, TOTALS_X + 4, cursorY + y);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...COLOR_DARK);
            doc.text(formatEur(val), TOTALS_X + TOTALS_W - 4, cursorY + y, { align: 'right' });
        };
        rigaTot('Imponibile soggetto a IVA', totali.soggetto, 8);
        rigaTot('Spese escluse da IVA', totali.esclusi, 16);
        rigaTot(`IVA ${aliquotaIva}%`, ivaProforma, 24);

        doc.setDrawColor(...COLOR_ACCENT);
        doc.setLineWidth(0.5);
        doc.line(TOTALS_X + 4, cursorY + 28, TOTALS_X + TOTALS_W - 4, cursorY + 28);

        doc.setFillColor(...COLOR_PRIMARY);
        doc.roundedRect(TOTALS_X, cursorY + 29, TOTALS_W, 15, 0, 0, 'F');
        doc.roundedRect(TOTALS_X, cursorY + 29, TOTALS_W, 15, 2, 2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...COLOR_WHITE);
        doc.text(labelTotale, TOTALS_X + 4, cursorY + 39);
        doc.setFontSize(13);
        doc.text(formatEur(totaleProforma), TOTALS_X + TOTALS_W - 4, cursorY + 39, { align: 'right' });

        cursorY += 49;
        } else {
        doc.setFillColor(...COLOR_LIGHT);
        doc.roundedRect(TOTALS_X, cursorY, TOTALS_W, 36, 2, 2, 'F');
        doc.setDrawColor(209, 213, 219);
        doc.setLineWidth(0.3);
        doc.roundedRect(TOTALS_X, cursorY, TOTALS_W, 36, 2, 2, 'S');

        // Imponibile
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...COLOR_MID);
        doc.text('Imponibile', TOTALS_X + 4, cursorY + 8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLOR_DARK);
        doc.text(formatEur(totaleBase), TOTALS_X + TOTALS_W - 4, cursorY + 8, { align: 'right' });

        // IVA
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLOR_MID);
        doc.text(`IVA ${aliquotaIva}%`, TOTALS_X + 4, cursorY + 16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLOR_DARK);
        doc.text(formatEur(ivaProforma), TOTALS_X + TOTALS_W - 4, cursorY + 16, { align: 'right' });

        // Divisore
        doc.setDrawColor(...COLOR_ACCENT);
        doc.setLineWidth(0.5);
        doc.line(TOTALS_X + 4, cursorY + 20, TOTALS_X + TOTALS_W - 4, cursorY + 20);

        // Barra TOTALE
        doc.setFillColor(...COLOR_PRIMARY);
        doc.roundedRect(TOTALS_X, cursorY + 21, TOTALS_W, 15, 0, 0, 'F');
        doc.roundedRect(TOTALS_X, cursorY + 21, TOTALS_W, 15, 2, 2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...COLOR_WHITE);
        doc.text(labelTotale, TOTALS_X + 4, cursorY + 31);
        doc.setFontSize(13);
        doc.text(formatEur(totaleProforma), TOTALS_X + TOTALS_W - 4, cursorY + 31, { align: 'right' });

        cursorY += 41;
        }

        // Pagamenti ricevuti e saldo: solo su mandati e servizi, il chiamante
        // può ometterli con mostraPagamenti.
        if (mostraPagamenti) {

        // Pagamenti ricevuti, nella colonna a sinistra del box totali.
        const totalsBottom = cursorY;
        const PAG_W = CONTENT_W - TOTALS_W - 6;
        let pagY = totalsTop + 3;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(...COLOR_MID);
        doc.text('PAGAMENTI RICEVUTI', MARGIN, pagY);
        pagY += 2;

        if (pagamenti.length === 0) {
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_MID);
            doc.text('Nessun pagamento registrato.', MARGIN, pagY + 4);
            cursorY = Math.max(totalsBottom, pagY + 9);
        } else {
            const pagamentiRows: any[] = pagamenti.map(p => ([
                p.data && p.data !== '—' ? new Date(p.data).toLocaleDateString('it-IT') : '—',
                p.descrizione || 'Pagamento ricevuto',
                formatEur(Number(p.importo) || 0),
            ]));
            autoTable(doc, {
                startY: pagY,
                head: [['Data', 'Descrizione', 'Importo']],
                body: pagamentiRows,
                theme: 'plain',
                margin: { left: MARGIN, right: PAGE_W - MARGIN - PAG_W, top: HEADER_H + 8, bottom: FOOTER_H + 8 },
                styles: { font: 'helvetica', fontSize: 8.5, textColor: COLOR_DARK as any, cellPadding: 1.5, lineColor: [229, 231, 235] as any, lineWidth: 0.1 },
                headStyles: { fillColor: [243, 244, 246] as any, textColor: COLOR_MID as any, fontStyle: 'bold', fontSize: 7.5 },
                columnStyles: {
                    0: { cellWidth: 20 },
                    1: { cellWidth: 'auto' },
                    2: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
                },
                didDrawPage: () => drawHeaderFooter((doc as any).internal.getCurrentPageInfo().pageNumber, doc.getNumberOfPages()),
            });
            let pagEnd = (doc as any).lastAutoTable.finalY + 4;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            doc.setTextColor(...COLOR_DARK);
            doc.text(`Totale incassato: ${formatEur(totaleIncassi)}`, MARGIN + PAG_W, pagEnd, { align: 'right' });
            pagEnd += 4;
            // Se la tabella è andata a capo pagina il box totali è rimasto sulla precedente.
            const samePage = (doc as any).internal.getCurrentPageInfo().pageNumber === totalsPage;
            cursorY = samePage ? Math.max(totalsBottom, pagEnd) : pagEnd;
        }

        // Saldo da incassare (solo modalità completo)
        if (mode === 'completo') {
            ensureSpace(16);
            const saldoColor: [number, number, number] = isSaldatoLocal ? [22, 163, 74] : [220, 38, 38];
            doc.setFillColor(...saldoColor);
            doc.rect(MARGIN, cursorY, CONTENT_W, 14, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(...COLOR_WHITE);
            doc.text(isSaldatoLocal ? 'SALDATO' : 'SALDO DA VERSARE', MARGIN + 4, cursorY + 9);
            doc.setFontSize(14);
            doc.text(formatEur(Math.abs(saldoDaIncassareLocal)), PAGE_W - MARGIN - 4, cursorY + 9, { align: 'right' });
            cursorY += 16;
        }
        }

        // Modalità di pagamento (IBAN/bonifico), come nel preventivo
        {
            const iban         = (settings as any)?.iban         || '__________________________';
            const intestatario = (settings as any)?.intestatario || '______';
            const bancaNome    = (settings as any)?.bancaNome    || '';

            // Dicitura fiscale e modalità di pagamento affiancate.
            const COL_W = (CONTENT_W - 4) / 2;
            const PAY_X = MARGIN + COL_W + 4;
            const testoStandard =
                'Documento non valido ai fini fiscali e IVA. La presente proforma viene emessa ' +
                'esclusivamente quale richiesta di pagamento/riepilogo degli importi dovuti. ' +
                'A seguito dell\'avvenuto pagamento sarà emessa regolare fattura elettronica ' +
                'nei termini di legge.';
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(8);
            const splitTesto = doc.splitTextToSize(testoStandard, COL_W - 8);
            doc.setFont('helvetica', 'normal');
            const linePayLines = [
                'Si invita a voler effettuare il pagamento mediante bonifico bancario alle seguenti coordinate:',
                `Intestatario: ${intestatario}`,
                bancaNome ? `Banca: ${bancaNome}` : null,
                `IBAN: ${iban}`,
                `Causale: pagamento proforma ${docNumber}`,
            ].filter(Boolean) as string[];
            const splitPay = linePayLines.flatMap(l => doc.splitTextToSize(l, COL_W - 8));
            const testoH = 5 + splitTesto.length * 3.6 + 2;
            const payH = 9 + splitPay.length * 3.6 + 2;
            const boxH = Math.max(testoH, payH);
            ensureSpace(boxH);

            doc.setDrawColor(209, 213, 219);
            doc.setFillColor(...COLOR_LIGHT);
            doc.setLineWidth(0.3);
            doc.roundedRect(MARGIN, cursorY, COL_W, boxH, 2, 2, 'FD');
            doc.roundedRect(PAY_X, cursorY, COL_W, boxH, 2, 2, 'FD');

            doc.setFont('helvetica', 'italic');
            doc.setFontSize(8);
            doc.setTextColor(...COLOR_MID);
            doc.text(splitTesto, MARGIN + 4, cursorY + 5, { lineHeightFactor: 1.25 });

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_DARK);
            doc.text('MODALITÀ DI PAGAMENTO', PAY_X + 4, cursorY + 5.5);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(...COLOR_MID);
            doc.text(splitPay, PAY_X + 4, cursorY + 10.5, { lineHeightFactor: 1.25 });

            cursorY += boxH + 4;
        }

        // Nessun disclaimer "valido 30 giorni": questo PDF è sempre una proforma.

        // Header su ogni pagina con la numerazione definitiva
        const totalPages = doc.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            drawHeaderFooter(i, totalPages);
        }

        const filename = `Proforma_${safeName}_${docNumber}.pdf`;
        const blob = doc.output('blob');
        return { blob, filename };
    } catch (e) {
        console.error('[generaProformaPdf] errore:', e);
        return null;
    }
};
